import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, TrendingUp, TrendingDown, Minus, ChevronDown, GitCompare } from 'lucide-react';
import { AVP, Snapshot } from '../types';
import { formatCurrency, formatPercentage } from '../utils/formatters';
interface Period {
  id: string;
  label: string;
  leaderboard: AVP[];
}
interface CompareModalProps {
  isOpen: boolean;
  onClose: () => void;
  periods: Period[];
}
function PeriodSelector({
  value,
  onChange,
  periods,
  exclude
}: {
  value: string;
  onChange: (id: string) => void;
  periods: Period[];
  exclude?: string;
}) {
  const [open, setOpen] = useState(false);
  const current = periods.find(p => p.id === value);
  return <div className="relative w-full" data-id="element-134">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-left hover:border-white/20 transition-colors" data-id="element-135">
        <div data-id="element-136">
          <p className="text-xs text-slate-500 mb-0.5" data-id="element-137">Period</p>
          <p className="text-white font-semibold" data-id="element-138">{current?.label}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-139" />
      </button>
      {open && <div className="absolute left-0 right-0 top-full mt-2 bg-[#1a2138] border border-white/10 rounded-xl p-1 z-10 shadow-2xl" data-id="element-140">
          {periods.map(p => <button key={p.id} disabled={p.id === exclude} onClick={() => {
        onChange(p.id);
        setOpen(false);
      }} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${p.id === value ? 'bg-indigo-500/20 text-indigo-200' : p.id === exclude ? 'text-slate-600 cursor-not-allowed' : 'text-slate-300 hover:bg-white/5'}`} data-id="element-141">
              {p.label}
            </button>)}
        </div>}
    </div>;
}
function DeltaBadge({
  delta
}: {
  delta: number;
}) {
  if (delta === 0 || !isFinite(delta)) {
    return <div className="inline-flex items-center gap-1 text-slate-400 text-xs font-medium" data-id="element-142">
        <Minus className="w-3 h-3" data-id="element-143" /> 0%
      </div>;
  }
  const positive = delta > 0;
  return <div className={`inline-flex items-center gap-1 text-xs font-medium ${positive ? 'text-emerald-400' : 'text-rose-400'}`} data-id="element-144">
      {positive ? <TrendingUp className="w-3 h-3" data-id="element-145" /> : <TrendingDown className="w-3 h-3" data-id="element-146" />}
      {formatPercentage(delta)}
    </div>;
}
function aggregate(leaderboard: AVP[]) {
  return leaderboard.reduce((acc, a) => {
    acc.volume += a.loanVolume;
    acc.revenue += a.totalRevenue;
    acc.loans += a.loansClosed;
    return acc;
  }, {
    volume: 0,
    revenue: 0,
    loans: 0
  });
}
function pct(a: number, b: number) {
  if (b === 0) return 0;
  return (a - b) / b * 100;
}
export function CompareModal({
  isOpen,
  onClose,
  periods
}: CompareModalProps) {
  const [periodAId, setPeriodAId] = useState(periods[0]?.id || '');
  const [periodBId, setPeriodBId] = useState(periods[1]?.id || '');
  const periodA = periods.find(p => p.id === periodAId);
  const periodB = periods.find(p => p.id === periodBId);
  const comparison = useMemo(() => {
    if (!periodA || !periodB) return null;
    const aggA = aggregate(periodA.leaderboard);
    const aggB = aggregate(periodB.leaderboard);
    // Build AVP-level diff
    const bMap = new Map(periodB.leaderboard.map(a => [a.id, a]));
    const rows = periodA.leaderboard.map(a => {
      const b = bMap.get(a.id);
      return {
        avp: a,
        rankA: a.rank,
        rankB: b?.rank ?? null,
        revA: a.totalRevenue,
        revB: b?.totalRevenue ?? 0,
        rankDelta: b ? b.rank - a.rank : 0,
        revDelta: b ? pct(a.totalRevenue, b.totalRevenue) : 0
      };
    }).sort((x, y) => x.rankA - y.rankA);
    return {
      kpis: [{
        label: 'Total Volume',
        a: aggA.volume,
        b: aggB.volume,
        delta: pct(aggA.volume, aggB.volume),
        format: 'currency' as const
      }, {
        label: 'Total Revenue',
        a: aggA.revenue,
        b: aggB.revenue,
        delta: pct(aggA.revenue, aggB.revenue),
        format: 'currency' as const
      }, {
        label: 'Total Loans',
        a: aggA.loans,
        b: aggB.loans,
        delta: pct(aggA.loans, aggB.loans),
        format: 'number' as const
      }, {
        label: 'Avg Revenue / Loan',
        a: aggA.loans ? aggA.revenue / aggA.loans : 0,
        b: aggB.loans ? aggB.revenue / aggB.loans : 0,
        delta: pct(aggA.loans ? aggA.revenue / aggA.loans : 0, aggB.loans ? aggB.revenue / aggB.loans : 0),
        format: 'currency' as const
      }],
      rows
    };
  }, [periodA, periodB]);
  return <AnimatePresence data-id="element-147">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" onClick={onClose} data-id="element-148" />
          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pointer-events-none overflow-y-auto" data-id="element-149">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} className="bg-[#141a2c] rounded-3xl my-8 max-w-5xl w-full shadow-2xl pointer-events-auto border border-white/10 relative overflow-hidden" data-id="element-150">
              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b border-white/5" data-id="element-151">
                <div data-id="element-152">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-medium mb-2" data-id="element-153">
                    <GitCompare className="w-3 h-3" data-id="element-154" /> Comparison View
                  </div>
                  <h2 className="text-2xl font-bold text-white" data-id="element-155">
                    Compare Performance
                  </h2>
                  <p className="text-sm text-slate-400 mt-1" data-id="element-156">
                    Side-by-side view across two periods
                  </p>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1" data-id="element-157">
                  <X className="w-5 h-5" data-id="element-158" />
                </button>
              </div>

              {/* Period selectors */}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center p-6 border-b border-white/5" data-id="element-159">
                <PeriodSelector value={periodAId} onChange={setPeriodAId} periods={periods} exclude={periodBId} data-id="element-160" />
                <ArrowRight className="w-5 h-5 text-slate-500 mx-auto hidden md:block" data-id="element-161" />
                <PeriodSelector value={periodBId} onChange={setPeriodBId} periods={periods} exclude={periodAId} data-id="element-162" />
              </div>

              {comparison && periodA && periodB && <>
                  {/* KPI deltas */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-6 border-b border-white/5" data-id="element-163">
                    {comparison.kpis.map(kpi => <div key={kpi.label} className="bg-black/30 rounded-xl p-4 border border-white/5" data-id="element-164">
                        <p className="text-xs text-slate-400 mb-2" data-id="element-165">
                          {kpi.label}
                        </p>
                        <p className="text-lg font-bold text-white mb-1" data-id="element-166">
                          {kpi.format === 'currency' ? formatCurrency(kpi.a, true) : kpi.a.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500 mb-2" data-id="element-167">
                          vs{' '}
                          {kpi.format === 'currency' ? formatCurrency(kpi.b, true) : kpi.b.toLocaleString()}
                        </p>
                        <DeltaBadge delta={kpi.delta} data-id="element-168" />
                      </div>)}
                  </div>

                  {/* AVP ranking shifts */}
                  <div className="p-6 max-h-[420px] overflow-y-auto" data-id="element-169">
                    <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-170">
                      AVP Ranking Shifts
                    </h3>
                    <table className="w-full text-left" data-id="element-171">
                      <thead data-id="element-172">
                        <tr className="border-b border-white/10 text-xs uppercase text-slate-500" data-id="element-173">
                          <th className="py-2 pr-3" data-id="element-174">Leader</th>
                          <th className="py-2 pr-3" data-id="element-175">Division</th>
                          <th className="py-2 pr-3 text-right" data-id="element-176">
                            {periodA.label} Rank
                          </th>
                          <th className="py-2 pr-3 text-right" data-id="element-177">
                            {periodB.label} Rank
                          </th>
                          <th className="py-2 pr-3 text-right" data-id="element-178">Movement</th>
                          <th className="py-2 text-right" data-id="element-179">Revenue Δ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5" data-id="element-180">
                        {comparison.rows.map(row => {
                    const moved = row.rankDelta;
                    const movementColor = moved > 0 ? 'text-emerald-400' : moved < 0 ? 'text-rose-400' : 'text-slate-500';
                    const movementLabel = moved > 0 ? `▲ ${moved}` : moved < 0 ? `▼ ${Math.abs(moved)}` : '–';
                    return <tr key={row.avp.id} className="hover:bg-white/[0.02] transition-colors" data-id="element-181">
                              <td className="py-3 pr-3" data-id="element-182">
                                <div className="flex items-center gap-2" data-id="element-183">
                                  <div className="w-7 h-7 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-300 text-xs font-semibold" data-id="element-184">
                                    {row.avp.initials}
                                  </div>
                                  <span className="text-sm text-white font-medium" data-id="element-185">
                                    {row.avp.name}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 pr-3 text-sm text-slate-400" data-id="element-186">
                                {row.avp.division}
                              </td>
                              <td className="py-3 pr-3 text-sm text-white text-right" data-id="element-187">
                                #{row.rankA}
                              </td>
                              <td className="py-3 pr-3 text-sm text-slate-400 text-right" data-id="element-188">
                                {row.rankB ? `#${row.rankB}` : '—'}
                              </td>
                              <td className={`py-3 pr-3 text-sm font-medium text-right ${movementColor}`} data-id="element-189">
                                {movementLabel}
                              </td>
                              <td className="py-3 text-right" data-id="element-190">
                                <DeltaBadge delta={row.revDelta} data-id="element-191" />
                              </td>
                            </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                </>}

              {/* Footer */}
              <div className="p-6 border-t border-white/5 flex items-center justify-end bg-black/10" data-id="element-192">
                <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition-colors border border-white/10" data-id="element-193">
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}